import React, { FC } from "react";
import { styled } from "styled-components";
import { font } from "../../lib/sharedStyles";
import { FormattedVariant } from "./BundleWrapper";

interface BundleVariantTileProps {
  variant: FormattedVariant;
}

interface TileProps {
  brand: 'fender' | 'jackson' | 'presonus';
}
interface ImageContainerProps {
  rotate?: boolean;
  brand: 'fender' | 'jackson' | 'presonus';
}
interface ProductInfoContainerProps {
  rotate?: boolean;
  brand: 'fender' | 'jackson' | 'presonus';
}

interface ProductInfoProps {
  brand: 'fender' | 'jackson' | 'presonus';
}

const brandStyles = {
  fender: {
    fontFamily: 'Instrument Sans',
    color: '#191a1f',
    fontSize: '14px',
    fontWeight: 600,
    filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))',
    tileBackground: 'linear-gradient(180deg, #E5E5E5 0%, #E5E5E5 13.7%, #EFF0F0 59.6%, #F7F7F7 100%)',
    productInfoBackground: 'initial',
    productInfoBorderRadius: 'initial',
    tileBorderRadius: 'initial',
  },
  presonus: {
    fontFamily: 'HelveticaNowDisplay',
    color: 'rgb(26, 27, 27)',
    fontSize: '14px',
    fontWeight: 500,
    filter: 'initial',
    tileBackground: 'rgb(243, 243, 243)',
    productInfoBackground: '#FFFFFF',
    productInfoBorderRadius: '16px',
    tileBorderRadius: '16px',
  },
  jackson: {
    fontFamily: 'Palanquin Dark',
    color: '#020202',
    fontSize: '1rem',
    fontWeight: 500,
    filter: 'initial',
    tileBackground: '#fff',
    productInfoBackground: '#F1F1F1',
    productInfoBorderRadius: 'initial',
    tileBorderRadius: 'initial'
  },
};

const Tile = styled.a<TileProps>`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  background: ${({ brand }) => brandStyles[brand].tileBackground};
  border-radius: ${({ brand }) => brandStyles[brand].tileBorderRadius};
  @media (min-width: 1024px) {
    justify-content: space-between;
    height: 100%;
  }
`;

const ImageContainer = styled.div<ImageContainerProps>`
  position: relative;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  filter: ${({ brand }) => brandStyles[brand].filter};

  aspect-ratio: 1 / 1.3;
  padding: 24% 16%;

  img {
    width: 100%;
    height: auto;
    object-fit: contain;
    filter: ${({ brand }) => brandStyles[brand].filter};
    transition: transform 300ms ease;

    ${({ rotate }) =>
    rotate &&
    `
        transform: rotate(-90deg) scale(1.5);
        transform-origin: center;
      `}
  }
`;


const ProductInfoContainer = styled.div<ProductInfoContainerProps>`
  background-color: ${({ brand }) => brandStyles[brand].productInfoBackground};
  padding: 16px;
  padding-top: 16px;
`;

interface ProductInfoProps {
  brand: 'fender' | 'presonus' | 'jackson';
}



const ProductInfo = styled.p<ProductInfoProps>`
  margin: 0;
  letter-spacing: 0.14px;
  font-family: ${({ brand }) => brandStyles[brand].fontFamily};
  color: ${({ brand }) => brandStyles[brand].color};
  font-size: ${({ brand }) => brandStyles[brand].fontSize};
  font-weight: ${({ brand }) => brandStyles[brand].fontWeight};
`;


export const BundleVariantTile: FC<BundleVariantTileProps> = ({ variant }) => {
  let rotate = false;

  variant.tags?.forEach(tag => {
    if (
      (tag.includes('guitar') || tag.includes('bass')) &&
      !tag.includes('amplifier') &&
      variant.product_type !== 'Guitar and Bass Parts'
    ) {
      rotate = true;
    }
  });

  const dataElement = document.getElementById('bundle-carousel');
  const locale = dataElement?.getAttribute('data-locale') || 'en-US';
  const priceNumber = parseFloat(variant.price.amount);
  const formattedPrice = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: variant.price.currencyCode,
  }).format(priceNumber);

  let brand: 'fender' | 'jackson' | 'presonus' = 'fender';
  if (window.location.href.includes('jackson')) {
    brand = 'jackson';
  } else if (window.location.href.includes('presonus')) {
    brand = 'presonus';
  }

  return (
    <Tile brand={brand} href={variant.url} data-sku={variant.sku ?? ""} data-variant-id={variant.id}>
      <ImageContainer brand={brand} rotate={rotate}>
        <img src={variant.imageUrl} alt={variant.title} />
      </ImageContainer>

      <ProductInfoContainer brand={brand} rotate={rotate}>
        <ProductInfo brand={brand}>{variant.title}</ProductInfo>
        <ProductInfo brand={brand}>{formattedPrice}</ProductInfo>
      </ProductInfoContainer>
    </Tile>
  );
};

export default BundleVariantTile;
